import {
  DropdownBasic,
  EmailInput,
  Input,
  TelInput,
  TextInput,
} from '@designsforhealth/dfh-react-components'
import { addYears } from 'date-fns'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { firstValueFrom } from 'rxjs'
import { useReCaptcha } from '../../lib/auth/hooks/useReCaptcha'
import { langCodes } from '../../lib/config/lang'
import { logAndCaptureException } from '../../lib/monitoring'
import {
  RegistrationAddressFieldValues,
  RegistrationData,
  RegistrationFieldValues,
  RegistrationStudentSelectOptions,
  RegistrationStudentUserMetadata,
  SubmitDataValidationHandler,
  useRegistrationForm,
  useSetLoginFactory,
  validatePasswordsMatch,
  validateTerms,
} from '../../lib/registration'
import { useLoginAfterRegistration } from '../../lib/registration/request/login-after'
import {
  setStudentRegistrationInput,
  studentRegistrationRequest$,
  useLatestStudentRegistrationInput,
} from '../../lib/registration/request/student'
import {
  formDisabledStates,
  formProcessingStates,
  useRegistrationState,
} from '../../lib/registration/state'
import { DocumentType, SpecialtyAction } from '../../lib/form'
import { useSiteSettings } from '../../lib/sanity/hooks/useSiteSettings'
import { LoginAfterRegistrationErrorMessage } from './LoginAfterRegistrationErrorMessage'
import { FileUpload } from '../upload/FileUpload'
import { RegistrationFormAddress } from './RegistrationFormAddress'
import { RegistrationFormHelpButton } from './RegistrationFormHelpButton'
import { RegistrationFormPasswordInputs } from './RegistrationFormPasswordInputs'
import { RegistrationFormReCaptcha } from './RegistrationFormReCaptcha'
import { RegistrationFormSimpleTerms } from './RegistrationFormSimpleTerms'
import { RegistrationFormSubmit } from './RegistrationFormSubmit'
import { RegistrationMessage } from './RegistrationMessage'
import * as Styled from './styled'
import { RegistrationUserActionRequired } from './RegistrationUserActionRequired'
import {
  EMAIL_MAX_LENGTH,
  emailMaxLengthRule,
  FIRST_NAME_MAX_LENGTH,
  firstNameMaxLengthRule,
  LAST_NAME_MAX_LENGTH,
  lastNameMaxLengthRule,
  PHONE_MAX_LENGTH,
  PHONE_MIN_LENGTH,
  phoneMaxLengthRule,
  phoneMinLengthRule,
} from '../../lib/form/fieldLengthRules'

const now = new Date()
const tenYrsFromNow = addYears(now, 10)

export type RegistrationFormStudentProps = {
  setSubmitting?: (submitting: boolean) => void
}

type RegistrationStudentFieldValues = RegistrationFieldValues &
  RegistrationAddressFieldValues & {
    phone: string
    graduationDate: string
    universityName: { label: string; value: string }
    universityNameOther?: string
    certificateUrl?: string
    specialty: {
      label: string
      value: string
      studentAction?: SpecialtyAction
      studentDocument?: DocumentType
    }
  }

const parseFieldValuesToRequestData = ({
  firstName,
  lastName,
  email,
  password,
  phone,
  graduationDate,
  universityName,
  universityNameOther,
  certificateUrl,
  specialty,
  street1,
  street2,
  city,
  state,
  zipCode,
  country,
}: RegistrationStudentFieldValues): RegistrationData & RegistrationStudentUserMetadata => {
  return {
    firstName,
    lastName,
    email,
    password,
    type: 'student',
    phone,
    graduation_date: graduationDate,
    university_name: universityNameOther || universityName.value,
    certificate_url: certificateUrl,
    specialty: JSON.stringify(specialty),
    address: JSON.stringify({
      street1,
      street2,
      city,
      state,
      zip_code: zipCode,
      country,
    }),
  }
}

const submitDataValidationHandlers: ReadonlyArray<
  SubmitDataValidationHandler<RegistrationStudentFieldValues>
> = [validatePasswordsMatch, validateTerms]

export const RegistrationFormStudent: React.VFC<RegistrationFormStudentProps> = ({
  setSubmitting = () => {
    // noop
  },
}) => {
  useLatestStudentRegistrationInput()
  useLoginAfterRegistration()
  useSetLoginFactory()

  const [
    { messages, dismissMessage, patterns },
    {
      register,
      watch,
      setValue,
      handleSubmit,
      errors,
      control,
      formState: { isSubmitting },
    },
  ] = useRegistrationForm<RegistrationStudentFieldValues>({ submitDataValidationHandlers })

  const formErrorsAlertRef = useRef<HTMLFormElement>(null)

  const currentUniversityName = watch('universityName')
  const currentSpecialty = watch('specialty')
  const { lang } = useSiteSettings() || {}
  const [selectOptions, setSelectOptions] = useState<RegistrationStudentSelectOptions>({
    specialty: [],
    university: [],
  })

  useEffect(() => {
    switch (lang) {
      case langCodes.en_CA:
        import('../../lib/registration/options/en-ca/student')
          .then((options) => setSelectOptions(options.default))
          .catch(logAndCaptureException)
        break
      default:
        import('../../lib/registration/options/en-us/student')
          .then((options) => setSelectOptions(options.default))
          .catch(logAndCaptureException)
        break
    }
  }, [lang])

  const { name: registrationState } = useRegistrationState()
  const processing = isSubmitting || formProcessingStates.includes(registrationState)
  const disabled = processing || formDisabledStates.includes(registrationState)

  const { captchaError, validateCaptcha, reCaptchaProps } = useReCaptcha()

  const scrollErrorsRefIntoView = () => {
    const formErrorsEl = formErrorsAlertRef.current
    if (formErrorsEl) {
      formErrorsEl.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const onSubmit = useCallback(
    async (fieldValues: RegistrationStudentFieldValues) => {
      const isValidCaptcha = !!validateCaptcha()
      if (!isValidCaptcha) {
        return
      }
      setStudentRegistrationInput({ input: parseFieldValuesToRequestData(fieldValues) })
      await firstValueFrom(studentRegistrationRequest$)
    },
    [validateCaptcha]
  )

  useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      scrollErrorsRefIntoView()
    }
  }, [errors])

  useEffect(() => {
    setSubmitting(processing)
  }, [setSubmitting, processing])

  const userActionRequiredMessage = useMemo(
    () => messages.find((message) => !!message.actions?.includes('user_action_required')),
    [messages]
  )

  if (userActionRequiredMessage) {
    return <RegistrationUserActionRequired message={userActionRequiredMessage} />
  }

  return (
    <Styled.RegistrationForm noValidate onSubmit={handleSubmit(onSubmit)} ref={formErrorsAlertRef}>
      <Styled.Title>Student Registration</Styled.Title>
      {messages.map((message, index) => (
        <RegistrationMessage
          key={index}
          message={message}
          onDismiss={() => dismissMessage(message)}
        />
      ))}
      {registrationState === 'login_error' && <LoginAfterRegistrationErrorMessage />}
      <div>
        {errors && Object.keys(errors).length > 0 && (
          <Styled.Alert type="error">
            Please review the form for any missing or required field(s).
          </Styled.Alert>
        )}
        <Styled.FieldsRow>
          <Styled.HalfColumn>
            <TextInput
              autoComplete="given-name"
              label="First name"
              name="firstName"
              register={register}
              errors={errors}
              required
              disabled={disabled}
              defaultValue=""
              maxLength={FIRST_NAME_MAX_LENGTH}
              validations={firstNameMaxLengthRule}
            />
          </Styled.HalfColumn>
          <Styled.HalfColumn>
            <TextInput
              autoComplete="family-name"
              label="Last name"
              name="lastName"
              register={register}
              errors={errors}
              required
              disabled={disabled}
              defaultValue=""
              maxLength={LAST_NAME_MAX_LENGTH}
              validations={lastNameMaxLengthRule}
            />
          </Styled.HalfColumn>
        </Styled.FieldsRow>
        <Styled.FieldsRow>
          <EmailInput
            autoComplete="email"
            label="Email Address"
            name="email"
            register={register}
            errors={errors}
            required
            disabled={disabled}
            defaultValue=""
            maxLength={EMAIL_MAX_LENGTH}
            validations={{
              ...emailMaxLengthRule,
              pattern: patterns.email,
            }}
          />
        </Styled.FieldsRow>
        <Styled.FieldsRow>
          <TelInput
            autoComplete="tel"
            label="Phone"
            name="phone"
            register={register}
            errors={errors}
            required
            disabled={disabled}
            defaultValue=""
            maxLength={PHONE_MAX_LENGTH}
            minLength={PHONE_MIN_LENGTH}
            validations={{
              ...phoneMaxLengthRule,
              ...phoneMinLengthRule,
            }}
          />
        </Styled.FieldsRow>
        <RegistrationFormPasswordInputs
          disabled={disabled}
          defaultValue=""
          errors={errors}
          register={register}
          watch={watch}
        />
        <Styled.Title>
          <Styled.TitleHelper>Please tell us about your enrollment:</Styled.TitleHelper>
        </Styled.Title>
        <Styled.FieldsRow>
          <Input
            label="Expected Graduation Date"
            name="graduationDate"
            register={register}
            errors={errors}
            required
            disabled={disabled}
            defaultValue=" "
            min={now.toISOString().substring(0, 10)}
            max={tenYrsFromNow.toISOString().substring(0, 10)}
            type="date"
          />
        </Styled.FieldsRow>
        <Styled.FieldsRow>
          <DropdownBasic
            label="University Name"
            name="universityName"
            control={control}
            errors={errors}
            options={selectOptions.university}
            required
            disabled={disabled}
            defaultValue={undefined}
          />
          <RegistrationFormHelpButton>
            Please provide the name of your university. We will contact you regarding your Student
            I.D.
          </RegistrationFormHelpButton>
        </Styled.FieldsRow>
        {currentUniversityName?.value === 'Other' && (
          <Styled.FieldsRow>
            <TextInput
              label="University Name"
              name="universityNameOther"
              register={register}
              errors={errors}
              required
              disabled={disabled}
              defaultValue=""
            />
          </Styled.FieldsRow>
        )}
        <Styled.FieldsRow>
          <DropdownBasic
            label="Practice Specialty"
            name="specialty"
            control={control}
            errors={errors}
            options={selectOptions.specialty}
            required
            disabled={disabled}
            defaultValue={undefined}
          />
        </Styled.FieldsRow>
        {currentSpecialty?.studentAction === SpecialtyAction.Upload && (
          <FileUpload
            document={currentSpecialty?.studentDocument}
            name="certificateUrl"
            setValue={setValue}
            register={register}
            errors={errors}
            required
          />
        )}
        <Styled.Title>
          <Styled.TitleHelper>Mailing address:</Styled.TitleHelper>
        </Styled.Title>
        <RegistrationFormAddress
          disabled={disabled}
          register={register}
          errors={errors}
          watch={watch}
          control={control}
        />
        <Styled.FieldsRow $gutter>
          <RegistrationFormSimpleTerms
            register={register}
            linkComponents={[Styled.PrivacyNoticeLink, Styled.TermsOfUseLink]}
          />
        </Styled.FieldsRow>

        <RegistrationFormReCaptcha reCaptchaProps={reCaptchaProps} captchaError={captchaError} />

        <RegistrationFormSubmit disabled={disabled} processing={processing}>
          Register
        </RegistrationFormSubmit>
      </div>
    </Styled.RegistrationForm>
  )
}
